import type { ReactNode } from "react";
import { Alert, Button, Spinner, type PortalDashboard } from "@dearbill/shared";
import { DocumentPreview } from "./DocumentPreview";
import { documentSlug, documentsApiPath, signedByAnother } from "./documents";
import { SignatureForm } from "./SignatureForm";
import { SignedDocumentPanel } from "./SignedDocumentPanel";
import { SignBlocked } from "./SignBlocked";
import { useDocumentSubmit } from "./useDocumentSubmit";
import { usePdfBlob } from "./usePdfBlob";
import { useSignableDocument } from "./useSignableDocument";

/** Fallback intent statement for a document without its own consent text. */
const DEFAULT_INTENT =
  "I intend to sign this form electronically and agree to do business electronically.";

interface DocumentSignProps {
  session: string;
  /** The session's role, from the dashboard. */
  role: PortalDashboard["role"];
  /** The enrollment's full id or short id, from the sign link. */
  enrollmentId: string;
  /** The document's URL slug, e.g. `cms-1696`. */
  slug: string;
  /** Called when the session is rejected (401). */
  onSignOut: () => void;
  /** Leaves the sign page for the dashboard: a bad link, or the member is done. */
  onExit: () => void;
}

/**
 * The member-portal page for one signable document (`/forms/:id/sign/:slug`).
 *
 * It loads the enrollment's documents and picks the slug's. A signed document
 * shows its confirmation and signed copy. An unsigned one the session may not
 * sign shows why: one only another signer may sign (`signedByAnother`, such as
 * CMS-1696 in a caregiver session) or a 403 on submit names that signer, and
 * any other reads as not ready yet. Otherwise it shows the prefilled PDF, fetched as
 * an authed blob, and `SignatureForm`, whose intent checkbox carries the
 * server's `intentText` verbatim. `useDocumentSubmit` posts the signature
 * and handles its outcomes.
 */
export function DocumentSign({
  session,
  role,
  enrollmentId,
  slug,
  onSignOut,
  onExit,
}: DocumentSignProps): ReactNode {
  const { doc, loadError, setDoc, reload } = useSignableDocument(
    enrollmentId,
    slug,
    session,
    onSignOut,
    onExit,
  );
  const { busy, notice, forbidden, submit } = useDocumentSubmit({
    enrollmentId,
    session,
    setDoc,
    reload,
    onSignOut,
  });

  const ready = doc !== null && doc.status !== "signed" && doc.signable && !forbidden;
  const docPath = doc ? `${documentsApiPath(enrollmentId)}/${doc.documentId}` : null;
  const pdf = usePdfBlob(ready && docPath ? `${docPath}/sign-document` : null, session, onSignOut);

  if (loadError || pdf.failed) {
    return (
      <Alert tone="error" title="We couldn't load this document.">
        Please reload the page. If it keeps happening, use Contact us to reach our team.
      </Alert>
    );
  }
  if (doc?.status === "signed") return <SignedDocumentPanel doc={doc} onDone={onExit} />;
  if (doc && (!doc.signable || forbidden)) {
    return (
      <SignBlocked doc={doc} memberOnly={forbidden || signedByAnother(doc, role)} onDone={onExit} />
    );
  }
  if (!doc || !pdf.doc) {
    return (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your document…</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-h3 text-midnight font-serif">Review and sign</h1>
      <p className="text-body text-slate-teal mt-2">
        Signing as <strong className="text-midnight">{doc.signerName}</strong>. Please review the{" "}
        {doc.title} below, then add your signature.
      </p>
      <DocumentPreview
        title={doc.title}
        doc={pdf.doc}
        filename={`${documentSlug(doc.documentId)}.pdf`}
      />
      <SignatureForm
        initialName={doc.signerName}
        intentLabel={
          <span className="text-body text-midnight whitespace-pre-line">
            {doc.intentText ?? DEFAULT_INTENT}
          </span>
        }
        consentKey={doc.consentVersion}
        busy={busy}
        notice={
          notice && (
            <Alert tone={notice.tone} title={notice.title}>
              {notice.body}
            </Alert>
          )
        }
        onSubmit={(input) => submit(input, doc)}
      />
      <Button variant="ghost" size="md" className="mt-4" onClick={onExit}>
        Back to your dashboard
      </Button>
    </div>
  );
}
